import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Upload } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import NewDesignSelect from '../components/NewDesignSelect';
import Seo from '../seo/Seo';
import { useAuth } from '../context/AuthContext';
import { useFrameUpload } from '../hooks/useFrameUpload';
import {
  framesHaveDimensionMismatch,
  prepareVeoFloorPlanFrame,
  veoCanvasDimensionsForResolution,
} from '../lib/prepareVeoFloorPlanFrame';
import {
  cancelVeoFloorplanVideoJob,
  fetchVeoFloorplanVideoJobStatus,
  startVeoFloorplanVideoJob,
} from '../lib/requestVeoFloorplanVideo';

const DEFAULT_PROMPT = `A clean architectural visualization transition.

Begin with the exact supplied top-down 2D floor plan. Keep the floor plan geometry, wall positions, room proportions, openings, and camera framing completely unchanged.

The floor plan lines gradually become solid architectural walls. All walls rise vertically upward from their exact 2D positions, smoothly and simultaneously, forming a clean white 3D cutaway floor plan.

The floor surface gains subtle depth and realistic ambient shadows as the walls finish extruding. Maintain a fixed isometric top-down camera with no rotation, no zoom, no panning, and no change in composition.

The transformation must look precise, controlled, and professional, like architectural CAD geometry being converted into a 3D model.

No new rooms. No moving walls. No changing dimensions. No furniture. No people. No text. No labels. No extra doors or windows. No camera shake. No morphing or melting geometry.`;

const DURATION_OPTIONS = [
  { value: '4', label: '4 seconds' },
  { value: '6', label: '6 seconds' },
  { value: '8', label: '8 seconds' },
];

const RESOLUTION_OPTIONS = [
  { value: '720p', label: '720p' },
  { value: '1080p', label: '1080p' },
];

const MODEL_OPTIONS = [
  { value: 'veo-3.1-fast-generate-preview', label: 'Veo 3.1 Fast' },
  { value: 'veo-3.1-generate-preview', label: 'Veo 3.1' },
];

const POLL_INTERVAL_MS = 3000;

/**
 * @param {{
 *   label: string;
 *   inputId: string;
 *   upload: ReturnType<typeof useFrameUpload>;
 *   disabled?: boolean;
 * }} props
 */
function FrameUploadSection({ label, inputId, upload, disabled = false }) {
  const {
    file,
    previewUrl,
    dimensions,
    fileInputRef,
    handleFileChange,
    handleDrop,
    handleRemove,
    handleReplaceClick,
  } = upload;

  return (
    <div className="fp3d-test-upload-section">
      <label className="new-design-field-label" htmlFor={inputId}>
        {label}
      </label>
      <input
        ref={fileInputRef}
        id={inputId}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileChange}
        className="new-design-file-input"
        disabled={disabled}
      />
      {!previewUrl ? (
        <div className="new-design-upload-dropzone-wrap">
          <label
            htmlFor={inputId}
            className="upload-dropzone fp3d-test-upload-dropzone"
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleDrop}
          >
            <div className="upload-dropzone-inner">
              <span className="upload-dropzone-cloud" aria-hidden="true">
                <Upload size={38} strokeWidth={1.75} />
              </span>
              <span>Drag and drop or click to upload PNG, JPG, or WebP</span>
            </div>
          </label>
        </div>
      ) : (
        <div className="fp3d-test-upload-preview">
          <img src={previewUrl} alt={label} className="fp3d-test-upload-preview-img" />
          <p className="fp3d-test-upload-name">{file?.name}</p>
          {dimensions ? (
            <p className="new-design-field-helper">
              {dimensions.width} × {dimensions.height} px
            </p>
          ) : null}
          <div className="new-design-upload-mobile-summary-btns">
            <div className="new-design-upload-summary-btn-row">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleReplaceClick}
                disabled={disabled}
              >
                Replace
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleRemove}
                disabled={disabled}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatElapsed(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

function VeoFloorplanVideoTestPage() {
  const { user } = useAuth();
  const firstFrame = useFrameUpload('veo-first-frame');
  const lastFrame = useFrameUpload('veo-last-frame');

  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [durationSeconds, setDurationSeconds] = useState('4');
  const [resolution, setResolution] = useState('720p');
  const [model, setModel] = useState('veo-3.1-fast-generate-preview');

  const [generating, setGenerating] = useState(false);
  const [jobId, setJobId] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [jobStatus, setJobStatus] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [resultMeta, setResultMeta] = useState(null);
  const [safetyInfo, setSafetyInfo] = useState(null);
  const [submittedDimensions, setSubmittedDimensions] = useState(null);

  const pollTimerRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const videoRef = useRef(null);
  const generationStartedAtRef = useRef(0);

  const expectedSubmittedDimensions = useMemo(() => {
    if (!firstFrame.file || !lastFrame.file) return null;
    return veoCanvasDimensionsForResolution(resolution === '1080p' ? '1080p' : '720p');
  }, [firstFrame.file, lastFrame.file, resolution]);

  const showDimensionWarning = useMemo(
    () =>
      framesHaveDimensionMismatch({
        first: firstFrame.dimensions,
        last: lastFrame.dimensions,
      }),
    [firstFrame.dimensions, lastFrame.dimensions],
  );

  const resolutionDurationConflict =
    resolution === '1080p' && durationSeconds !== '8';

  const clearTimers = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    clearTimers();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, [clearTimers]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const pollJobStatus = useCallback(
    async (activeJobId) => {
      if (!user || !activeJobId) return;

      const result = await fetchVeoFloorplanVideoJobStatus(user, activeJobId, {
        signal: abortControllerRef.current?.signal,
      });

      if (!result.ok) {
        if (result.code === 'aborted') return;
        setError(result.error);
        setGenerating(false);
        stopPolling();
        return;
      }

      setStatusMessage(result.message || '');
      setJobStatus(result.status || '');

      if (result.status === 'completed' && result.videoUrl) {
        setVideoUrl(result.videoUrl);
        setResultMeta({
          model: result.model,
          durationSeconds: result.durationSeconds,
          resolution: result.resolution,
        });
        setSafetyInfo(result.safetyInfo);
        setGenerating(false);
        stopPolling();
        return;
      }

      if (result.status === 'failed') {
        setError(result.error || 'Video generation failed.');
        setGenerating(false);
        stopPolling();
      }
    },
    [user, stopPolling],
  );

  const startPolling = useCallback(
    (activeJobId) => {
      clearTimers();
      abortControllerRef.current = new AbortController();

      void pollJobStatus(activeJobId);
      pollTimerRef.current = setInterval(() => {
        void pollJobStatus(activeJobId);
      }, POLL_INTERVAL_MS);

      generationStartedAtRef.current = Date.now();
      setElapsedSeconds(0);
      elapsedTimerRef.current = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - generationStartedAtRef.current) / 1000));
      }, 1000);
    },
    [clearTimers, pollJobStatus],
  );

  const handleGenerate = useCallback(async () => {
    if (!user) {
      setError('You must be signed in to generate.');
      return;
    }
    if (!firstFrame.file || !lastFrame.file) {
      setError('Upload both the first and last frames before generating.');
      return;
    }
    if (!prompt.trim()) {
      setError('Enter an animation prompt.');
      return;
    }
    if (resolutionDurationConflict) {
      setError('The selected resolution is not supported for this duration or model.');
      return;
    }

    setGenerating(true);
    setError('');
    setVideoUrl('');
    setResultMeta(null);
    setSafetyInfo(null);
    setJobId('');
    setStatusMessage('Uploading input frames');
    setJobStatus('processing');
    stopPolling();

    try {
      const canvasSize = veoCanvasDimensionsForResolution(
        resolution === '1080p' ? '1080p' : '720p',
      );

      const [preparedFirst, preparedLast] = await Promise.all([
        prepareVeoFloorPlanFrame(firstFrame.file, canvasSize),
        prepareVeoFloorPlanFrame(lastFrame.file, canvasSize),
      ]);

      setSubmittedDimensions({
        width: preparedFirst.meta.submittedWidth,
        height: preparedFirst.meta.submittedHeight,
      });

      const startResult = await startVeoFloorplanVideoJob(user, {
        firstFrameBlob: preparedFirst.blob,
        firstFrameName: firstFrame.file.name || 'first-frame.jpg',
        lastFrameBlob: preparedLast.blob,
        lastFrameName: lastFrame.file.name || 'last-frame.jpg',
        prompt: prompt.trim(),
        durationSeconds,
        resolution,
        model,
      });

      if (!startResult.ok) {
        setError(startResult.error);
        setGenerating(false);
        return;
      }

      setJobId(startResult.jobId);
      setStatusMessage('Starting Veo generation');
      startPolling(startResult.jobId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not start video generation.';
      setError(message);
      setGenerating(false);
    }
  }, [
    user,
    firstFrame.file,
    lastFrame.file,
    prompt,
    durationSeconds,
    resolution,
    model,
    resolutionDurationConflict,
    stopPolling,
    startPolling,
  ]);

  const handleCancel = useCallback(async () => {
    if (!user || !jobId) {
      stopPolling();
      setGenerating(false);
      setStatusMessage('Cancelled');
      return;
    }

    stopPolling();
    const result = await cancelVeoFloorplanVideoJob(user, jobId);
    setGenerating(false);
    setJobStatus('failed');
    setStatusMessage('Cancelled');
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setError(result.error || 'Video generation was cancelled.');
  }, [user, jobId, stopPolling]);

  const handleReset = useCallback(() => {
    stopPolling();
    firstFrame.handleRemove();
    lastFrame.handleRemove();
    setPrompt(DEFAULT_PROMPT);
    setDurationSeconds('4');
    setResolution('720p');
    setModel('veo-3.1-fast-generate-preview');
    setGenerating(false);
    setJobId('');
    setStatusMessage('');
    setJobStatus('');
    setElapsedSeconds(0);
    setError('');
    setVideoUrl('');
    setResultMeta(null);
    setSafetyInfo(null);
    setSubmittedDimensions(null);
  }, [firstFrame, lastFrame, stopPolling]);

  const handleReplay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    void video.play();
  }, []);

  const canGenerate =
    Boolean(firstFrame.file && lastFrame.file && prompt.trim()) &&
    !generating &&
    !resolutionDurationConflict;

  return (
    <>
      <Seo
        title="Veo Floor Plan Animation Test | MyRoomsAI"
        description="Generate a video transition between a 2D floor plan and a 3D floor plan using Google Veo 3.1."
        canonicalPath="/test/veo-floorplan-video"
        noindex
      />
      <Navbar />
      <main className="dashboard-page new-design-page fp3d-test-page nanobanan-test-page">
        <div className="container">
          <Link to="/test-pages" className="tool-category-tab new-design-back-to-tools">
            <ArrowLeft size={16} strokeWidth={2} aria-hidden />
            Back to test pages
          </Link>

          <header className="tool-hub-header category-hub-header new-design-workspace-header">
            <h1 className="tool-hub-heading">
              <span className="new-design-page-heading-accent">Veo Floor Plan Animation</span> Test
            </h1>
            <p className="new-design-field-helper">
              Generate a video transition between a 2D floor plan and a 3D floor plan using Google
              Veo 3.1. No credits are deducted on this test page.
            </p>
          </header>

          {error ? (
            <p className="dashboard-inline-error" role="alert">
              {error}
            </p>
          ) : null}

          <section className="dashboard-grid design-dual-layout nanobanan-test-workspace">
            <article className="card new-design-setup-card fp3d-test-setup-card">
              <h2 className="new-2d-project-section-title">Generation controls</h2>

              <div className="new-design-control-group">
                <label className="new-design-field-label" htmlFor="veo-animation-prompt">
                  Animation Prompt
                </label>
                <textarea
                  id="veo-animation-prompt"
                  className="dashboard-notes new-design-notes nanobanan-test-prompt"
                  rows={14}
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                  disabled={generating}
                />
                <p className="new-design-field-helper">{prompt.length} characters</p>
              </div>

              <FrameUploadSection
                label="First Frame — 2D Floor Plan"
                inputId="veo-first-frame"
                upload={firstFrame}
                disabled={generating}
              />

              <FrameUploadSection
                label="Last Frame — 3D Floor Plan"
                inputId="veo-last-frame"
                upload={lastFrame}
                disabled={generating}
              />

              {showDimensionWarning ? (
                <p className="dashboard-inline-error" role="status">
                  For the most stable transition, both frames should use the same dimensions, aspect
                  ratio, crop, and camera framing.
                </p>
              ) : null}

              {(submittedDimensions || expectedSubmittedDimensions) ? (
                <p className="new-design-field-helper">
                  Submitted dimensions:{' '}
                  {(submittedDimensions || expectedSubmittedDimensions).width} ×{' '}
                  {(submittedDimensions || expectedSubmittedDimensions).height} px (16:9 canvas)
                </p>
              ) : null}

              <div className="new-design-control-group fp3d-test-options-fields">
                <label className="new-design-field-label" htmlFor="veo-duration">
                  Duration
                </label>
                <NewDesignSelect
                  id="veo-duration"
                  value={durationSeconds}
                  onChange={setDurationSeconds}
                  options={DURATION_OPTIONS}
                  disabled={generating}
                />
              </div>

              <div className="new-design-control-group fp3d-test-options-fields">
                <label className="new-design-field-label" htmlFor="veo-resolution">
                  Resolution
                </label>
                <NewDesignSelect
                  id="veo-resolution"
                  value={resolution}
                  onChange={setResolution}
                  options={RESOLUTION_OPTIONS}
                  disabled={generating}
                />
                {resolutionDurationConflict ? (
                  <p className="new-design-field-helper">
                    1080p requires an 8-second duration.
                  </p>
                ) : null}
              </div>

              <div className="new-design-control-group fp3d-test-options-fields">
                <label className="new-design-field-label" htmlFor="veo-model">
                  Veo Model
                </label>
                <NewDesignSelect
                  id="veo-model"
                  value={model}
                  onChange={setModel}
                  options={MODEL_OPTIONS}
                  disabled={generating}
                />
              </div>

              <div className="new-design-upload-summary-btn-row">
                <button
                  type="button"
                  className="btn btn-primary full-width new-design-generate-btn fp3d-test-generate-btn"
                  disabled={!canGenerate}
                  onClick={handleGenerate}
                >
                  Generate Animation
                </button>
                <button
                  type="button"
                  className="btn btn-ghost full-width"
                  onClick={handleReset}
                  disabled={generating}
                >
                  Reset
                </button>
              </div>
            </article>

            <article className="card new-design-setup-card fp3d-test-setup-card">
              <h2 className="new-2d-project-section-title">Preview and status</h2>

              {generating ? (
                <div className="fp3d-test-preview-details">
                  <p className="new-2d-project-loading fp3d-test-loading" role="status">
                    {statusMessage || 'Video generation in progress'}
                  </p>
                  <p className="new-design-field-helper">
                    Elapsed time: {formatElapsed(elapsedSeconds)}
                  </p>
                  <p className="new-design-field-helper">
                    Closing or refreshing this page will interrupt local status polling. The server
                    may continue generating until completion.
                  </p>
                  <button type="button" className="btn btn-ghost" onClick={handleCancel}>
                    Cancel
                  </button>
                </div>
              ) : null}

              {!generating && videoUrl ? (
                <>
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    controls
                    playsInline
                    className="fp3d-test-upload-preview-img"
                    aria-label="Generated floor plan animation"
                  />
                  <div className="new-design-upload-summary-btn-row">
                    <button type="button" className="btn btn-ghost" onClick={handleReplay}>
                      Replay
                    </button>
                    <a href={videoUrl} download="veo-floorplan-animation.mp4" className="btn btn-outline">
                      Download Video
                    </a>
                  </div>
                  <div className="fp3d-test-preview-details">
                    <p className="new-design-field-helper">Status: Completed</p>
                    {resultMeta?.model ? (
                      <p className="new-design-field-helper">Model: {resultMeta.model}</p>
                    ) : null}
                    {resultMeta?.durationSeconds ? (
                      <p className="new-design-field-helper">
                        Duration: {resultMeta.durationSeconds} seconds
                      </p>
                    ) : null}
                    {resultMeta?.resolution ? (
                      <p className="new-design-field-helper">
                        Resolution: {resultMeta.resolution}
                      </p>
                    ) : null}
                    {safetyInfo?.filteredCount ? (
                      <p className="new-design-field-helper">
                        Safety filtering: {safetyInfo.filteredCount} item(s) filtered
                        {Array.isArray(safetyInfo.filteredReasons) &&
                        safetyInfo.filteredReasons.length > 0
                          ? ` — ${safetyInfo.filteredReasons.join('; ')}`
                          : ''}
                      </p>
                    ) : null}
                  </div>
                </>
              ) : null}

              {!generating && !videoUrl ? (
                <p className="new-design-field-helper">
                  {jobStatus === 'failed'
                    ? 'Generation failed. Adjust your inputs and try again.'
                    : 'Your generated video will appear here.'}
                </p>
              ) : null}
            </article>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}

export default VeoFloorplanVideoTestPage;
